/**
 * Push notification de-duplication.
 *
 * Regression guard: FCM and Capacitor both deliver the same notification, and
 * remounts used to re-register listeners, producing multiple identical toasts.
 */

export const PUSH_DEDUPE_WINDOW_MS = 15_000;

export interface PushDedupeStore {
  /** key -> timestamp (ms) of the last time the push was handled */
  seen: Map<string, number>;
}

export function createPushDedupeStore(): PushDedupeStore {
  return { seen: new Map() };
}

/** Builds a stable key for a push payload. */
export function pushKey(input: {
  notificationId?: string | number | null;
  id?: string | number | null;
  title?: string | null;
  body?: string | null;
}): string {
  if (input.notificationId !== undefined && input.notificationId !== null && input.notificationId !== '') {
    return String(input.notificationId);
  }
  if (input.id !== undefined && input.id !== null && input.id !== '') {
    return String(input.id);
  }
  return `${input.title ?? ''}|${input.body ?? ''}`;
}

/**
 * Returns false when the same push was already handled inside the dedupe
 * window. Expired keys are pruned on each call.
 */
export function shouldHandlePush(
  store: PushDedupeStore,
  key: string,
  now: number = Date.now(),
  windowMs: number = PUSH_DEDUPE_WINDOW_MS,
): boolean {
  for (const [k, ts] of store.seen) {
    if (now - ts > windowMs) store.seen.delete(k);
  }
  if (store.seen.has(key)) return false;
  store.seen.set(key, now);
  return true;
}

/** Stable toast id so repeated pushes replace instead of stacking. */
export function pushToastId(key: string): string {
  return `push-${key}`;
}
